import { supabase } from '@/lib/supabase';
import type { DocDocument, ExcelContent, ExcelSheet } from '@/lib/documents';

/** Marqueur enregistré dans `forms.structure` pour relier un formulaire à un classeur. */
export interface SheetLinkStructure {
  sheetDocumentId: number;
  sheetName?: string;
  createdAt?: string;
}

export interface SheetLink {
  formId: number;
  title: string;
  documentId: number;
}

/** Extrait l'identifiant de classeur d'une valeur `forms.structure`. */
export function sheetDocumentIdOf(structure: string | null | undefined): number | null {
  const raw = (structure || '').trim();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const value = Number(parsed.sheetDocumentId ?? parsed.excelDocumentId ?? parsed.documentId);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    const numeric = Number(raw);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
  }
}

/** URL publique de saisie d'un formulaire. */
export function publicFormUrl(formId: number): string {
  const basePath = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '').replace(/\/$/, '');
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://zifek.fr';
  return `${origin}${basePath}/forms/${formId}`;
}

/** Première feuille d'un contenu tableur (gère l'ancien format mono-feuille). */
export function firstSheetOf(content: Record<string, unknown> | null | undefined): ExcelSheet {
  const excel = (content || {}) as ExcelContent;
  if (Array.isArray(excel.sheets) && excel.sheets.length > 0) return excel.sheets[0];
  return {
    id: 'sheet-1',
    name: 'Feuille 1',
    cells: excel.cells || {},
    rows: excel.rows || 24,
    cols: excel.cols || 8,
  };
}

/** Libellés de colonnes = première ligne du classeur. */
export function sheetHeaders(content: Record<string, unknown> | null | undefined): string[] {
  const sheet = firstSheetOf(content);
  const cells = sheet.cells || {};
  const found: string[] = [];
  for (let c = 0; c < Math.max(sheet.cols || 8, 1); c += 1) {
    const value = (cells[`0,${c}`] || '').trim();
    if (value) found.push(value);
  }
  return found;
}

/** Retrouve le formulaire déjà relié à un classeur (s'il existe). */
export async function findLinkedForm(userId: number, documentId: number): Promise<SheetLink | null> {
  const { data, error } = await supabase
    .from('forms')
    .select('id, title, structure, commerceid, user_id')
    .eq('user_id', userId);
  if (error) return null;
  const rows = (data || []) as { id: number; title: string | null; structure: string | null }[];
  const match = rows.find((row) => sheetDocumentIdOf(row.structure) === documentId);
  if (!match) return null;
  return { formId: match.id, title: match.title || 'Formulaire', documentId };
}

/**
 * Crée un formulaire lié au classeur : un champ par colonne de la première
 * ligne, et un marqueur dans `forms.structure` pour l'écriture des réponses.
 */
export async function createLinkedForm(
  userId: number,
  doc: DocDocument,
): Promise<SheetLink> {
  const headers = sheetHeaders(doc.content);
  const labels = headers.length > 0
    ? headers
    : ['Nom', 'Email', 'Téléphone', 'Message'];
  const structure: SheetLinkStructure = {
    sheetDocumentId: doc.id,
    sheetName: firstSheetOf(doc.content).name,
    createdAt: new Date().toISOString(),
  };
  const title = `${doc.name} — réponses`;

  const { data: form, error } = await supabase
    .from('forms')
    .insert({
      user_id: userId,
      commerceid: userId,
      title,
      description: `Les réponses de ce formulaire sont ajoutées automatiquement dans les lignes du classeur « ${doc.name} ».`,
      form_json: '',
      structure: JSON.stringify(structure),
      coverimage: '',
      productname: '',
    })
    .select('id, title')
    .single();
  if (error) throw new Error(error.message || 'Création du formulaire impossible');

  const formRow = form as { id: number; title: string | null };
  const fields = labels.map((label, index) => ({
    form_id: formRow.id,
    label,
    type: /mail/i.test(label) ? 'email' : /(tel|phone|téléphone|mobile)/i.test(label) ? 'tel' : 'text',
    options: '',
    position: index,
    required: index === 0,
  }));
  const { error: fieldError } = await supabase.from('form_fields').insert(fields);
  if (fieldError) throw new Error(fieldError.message || 'Création des champs impossible');

  return { formId: formRow.id, title: formRow.title || title, documentId: doc.id };
}

/**
 * Envoie les réponses au classeur lié (fonction Backend, exécutée côté serveur
 * avec les droits du propriétaire du document).
 */
export async function syncAnswersToSheet(
  formId: number,
  answers: { label: string; value: string }[],
): Promise<{ ok: boolean; row?: number }> {
  if (!answers.length) return { ok: false };
  try {
    const { data, error } = await supabase.functions.invoke('zifek-form-to-sheet', {
      body: { formId, answers, submittedAt: Date.now() },
    });
    if (error) return { ok: false };
    const payload = data as { ok?: boolean; row?: number } | null;
    return { ok: !!payload?.ok, row: payload?.row };
  } catch {
    return { ok: false };
  }
}